import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  CheckUserAgreementStateAndRedirect,
  generateMinimalAppErrorMessage,
} from '@/root-app/utils/authenticatedApp.utils';
import AppLayout from '@/components/Layout/AppLayout';
import { getAccessToken } from '@/index';
import SectionLoader from '@/components/Loader/SectionLoader';
import {
  getBoardId,
  getUserAgreementState,
  getUserId,
  getUserPortalRole,
} from '@/core/web-storage/webStorage.utils';
import MinimalAppWithError from '@/root-app/MinimalAppWithError';
import Button from '@/components/Button/Button';
import tokens from '@/core/tokens.json';
import { usersApi } from '@/users/services/usersApi';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import { isValidPortalRole } from '@/authorization/utils/authorization.utils';
import useLogout from '@/hooks/useLogout';

function AuthenticatedApp() {
  const [appReady, setIsAppReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const [areClaimsNotSet, setAreClaimsNotSet] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [fetchUserAgreementState] = usersApi.useLazyGetUserAgreementStateQuery();
  const [createDbUserFromAdUser] = usersApi.useCreateDbUserFromAdUserMutation();
  const appInsights = useAppInsightsContext();
  const logout = useLogout();

  instance.addEventCallback(async (event) => {
    if (event.eventType === EventType.ACQUIRE_TOKEN_START) {
      try {
        const userId = getUserId();
        const userBoardId = getBoardId();
        const userPortalRole = getUserPortalRole();
        const userAgreementState = getUserAgreementState();

        if (!isValidPortalRole(userPortalRole) || !userId) {
          setAreClaimsNotSet(true);
          const error = new Error('Authenticated user has an invalid / missing PortalRole claim.');
          appInsights.trackException({
            error: error,
            exception: error,
            severityLevel: SeverityLevel.Error,
          });
          setIsAppReady(false);
          setIsError(true);
        }
        if (
          userPortalRole !== PORTAL_ROLES.ATA &&
          userPortalRole !== PORTAL_ROLES.ATRF &&
          !userBoardId
        ) {
          setIsAppReady(false);
          setIsError(true);
          setAreClaimsNotSet(true);
        }

        if (userAgreementState === null) {
          await CheckUserAgreementStateAndRedirect(
            userId,
            userPortalRole,
            fetchUserAgreementState,
            createDbUserFromAdUser,
            () => {
              setIsAppReady(true);
            },
            () => {
              setIsAppReady(false);
              setIsError(true);
            },
          );
        } else {
          setIsAppReady(true);
        }
      } catch {
        setIsAppReady(false);
        setIsError(true);
      }
    }
  });

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      void getAccessToken().catch(() => {
        setIsError(true);
      });
    }
  }, [inProgress, isAuthenticated, accounts, instance]);

  if (isError) {
    return (
      <MinimalAppWithError
        title={generateMinimalAppErrorMessage()}
        description={areClaimsNotSet ? tokens['CONTACT_ADMIN'] : undefined}
        cta={
          <Button
            variant="contained"
            onClick={() => logout()}
            title={tokens['MENU_ITEMS']['LOGOUT']}
          >
            {tokens['MENU_ITEMS']['LOGOUT']}
          </Button>
        }
      />
    );
  }
  return appReady ? <AppLayout /> : <SectionLoader />;
}

export default AuthenticatedApp;
